/* eslint-disable @typescript-eslint/no-explicit-any */
import clsx from 'clsx'
import NextImage from 'next/legacy/image'
import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import {
	DismissButton,
	FocusScope,
	useButton,
	useComboBox,
	useFilter,
	useListBox,
	useOption,
	useOverlay,
	type AriaButtonProps,
	type AriaListBoxOptions,
	type AriaOptionProps,
	type AriaOverlayProps,
} from 'react-aria'
import { useComboBoxState } from 'react-stately'
import SvgChevronDown from '../../svg/icons/ChevronDown'
import SvgCross from '../../svg/icons/Cross'
import { SuggestionModal } from '../shared/SuggestionModal'
import style from './ComboBox.module.sass'

export function ComboBox(
	props: any /* FIXME: any */ & {
		height: number | undefined
		hasPreview?: boolean
		name?: string
		selectedPreview?: boolean
		noOptionsMessage?: ReactNode
		hasMinLength?: boolean
		placeholder?: string | undefined
		clearInput: () => void
		imagePreview?: JSX.Element
		isArrowHidden?: boolean
		isResetedState?: any
	},
) {
	const { contains } = useFilter({ sensitivity: 'base' })
	const state = useComboBoxState({ ...props, defaultFilter: contains })
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isEmptyPopoverOpen, setIsEmptyPopoverOpen] = useState(false)
	const buttonRef = useRef(null)
	const inputRef = useRef<HTMLInputElement | null>(null)
	const listBoxRef = useRef(null)
	const popoverRef = useRef(null)
	const clearRef = useRef(null)

	const { buttonProps, inputProps, listBoxProps, labelProps } = useComboBox(
		{
			...props,
			inputRef,
			buttonRef,
			listBoxRef,
			popoverRef,
		},
		state,
	)
	const isMinLenghtValue = inputProps?.value && String(inputProps.value).length > 2

	useEffect(() => {
		if ([...state.collection].length === 0 && isMinLenghtValue) {
			setIsEmptyPopoverOpen(true)
		} else {
			setIsEmptyPopoverOpen(false)
		}
	}, [isMinLenghtValue, state.collection])

	useEffect(() => {
		if (props.isResetedState && props.isResetedState.length > 0 && Boolean(props.isResetedState[0])) {
			props.isResetedState[1](false)
			state.setSelectedKey('')
		}
	}, [props.isResetedState, props.state, state])

	const closeModal = useCallback(() => setIsModalOpen(false), [])

	return (
		<>
			<SuggestionModal isModalOpen={isModalOpen} handleClose={closeModal} />
			<div className={style.wrapper}>
				{props.label && (
					<label {...labelProps} htmlFor={props.placeholder}>
						{props.label}
					</label>
				)}
				<div className={style.root}>
					{props.imagePreview}
					<input
						aria-label={props.name ?? props.placeholder}
						aria-labelledby={props.name ?? props.placeholder}
						name={props.placehoder}
						placeholder={props.placeholder ?? ''}
						{...inputProps}
						ref={inputRef}
						className={clsx(style.input, props.selectedPreview && style.inputMoved)}
						style={{
							height: props.height ?? 38,
							boxShadow: 'none',
						}}
					/>
					{!props.isArrowHidden && (
						<ComboBoxButton {...buttonProps} buttonRef={buttonRef} disabled={!isMinLenghtValue}>
							<span style={{ padding: '0 2px' }}>
								<SvgChevronDown />
							</span>
						</ComboBoxButton>
					)}
					{state.inputValue && (
						<ComboBoxButton
							buttonRef={clearRef}
							disabled={false}
							onClick={() => {
								state.setSelectedKey('')
							}}
						>
							<span style={{ padding: '0 2px' }}>
								<SvgCross />
							</span>
						</ComboBoxButton>
					)}
					<div className={style.dropdown}>
						{state.isOpen && (props.hasMinLength ? isMinLenghtValue : true) && (
							<Popover popoverRef={popoverRef} isOpen={state.isOpen} onClose={state.close}>
								<ListBox {...listBoxProps} listBoxRef={listBoxRef} state={state} hasPreview={props.hasPreview} />
							</Popover>
						)}
						{isEmptyPopoverOpen && props.noOptionsMessage && (
							<Popover popoverRef={popoverRef} isOpen={state.isOpen} onClose={state.close}>
								{props.noOptionsMessage}
							</Popover>
						)}
					</div>
				</div>
			</div>
		</>
	)
}

function ComboBoxButton(
	props: AriaButtonProps<'button'> & {
		disabled: boolean
		buttonRef: RefObject<HTMLButtonElement>
		onClick?: React.MouseEventHandler<HTMLButtonElement>
	},
) {
	const ref = props.buttonRef
	const { buttonProps } = useButton(props, ref)
	return (
		<button
			{...buttonProps}
			ref={ref}
			className={style.button}
			disabled={props.disabled}
			onClick={props.onClick ?? buttonProps.onClick}
		>
			{props.children}
		</button>
	)
}

function Popover(props: AriaOverlayProps & { popoverRef: RefObject<HTMLDivElement>; children: ReactNode }) {
	const ref = useRef() as RefObject<HTMLDivElement>
	const { popoverRef = ref, isOpen, onClose } = props
	const { overlayProps } = useOverlay(
		{
			isOpen,
			onClose,
			shouldCloseOnBlur: true,
			isDismissable: true,
		},
		popoverRef,
	)

	return (
		<FocusScope restoreFocus>
			<div {...overlayProps} ref={popoverRef} className={style.listWrapper}>
				{props.children}
				<DismissButton onDismiss={onClose} />
			</div>
		</FocusScope>
	)
}

function ListBox(
	props: AriaListBoxOptions<unknown> & {
		listBoxRef: RefObject<HTMLUListElement>
		state: any /* FIXME: any */
		hasPreview?: boolean
	},
) {
	const ref = useRef() as RefObject<HTMLUListElement>
	const { listBoxRef = ref, state } = props
	const { listBoxProps } = useListBox(props, state, listBoxRef)

	return (
		<ul {...listBoxProps} ref={listBoxRef} className={style.list}>
			{[...state.collection].map((item) => (
				<Option key={item.key} item={item} state={state} hasPreview={props.hasPreview} />
			))}
		</ul>
	)
}

function Option(
	props: AriaOptionProps & {
		item: any /* FIXME: any */
		state: any /* FIXME: any */
		hasPreview?: boolean
	},
) {
	const ref = useRef() as RefObject<HTMLLIElement>
	const { optionProps, isSelected, isFocused, isDisabled } = useOption({ key: props.item.key }, props.state, ref)

	let backgroundColor
	let color = 'black'

	if (isSelected) {
		backgroundColor = '#A8A8A8' // $greyMedium
		color = '100D3D' //$primary
	} else if (isFocused) {
		backgroundColor = 'rgb(168,168,168, 0.5)' // $greyMediumLight
	} else if (isDisabled) {
		backgroundColor = '#FFF'
		color = 'gray'
	}

	return (
		<li
			{...optionProps}
			ref={ref}
			className={style.listItem}
			style={{
				background: backgroundColor,
				color: color,
			}}
		>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					minHeight: props.hasPreview ? '36px' : '24px',
				}}
			>
				{props.hasPreview && props.item.key !== 'undisclosed' ? (
					<>
						<div
							style={{
								display: 'inline-block',
								marginRight: '8px',
								color: 'black',
								minWidth: '40px',
							}}
						>
							<NextImage
								src={props.item.value.imageUrl ?? '/images/placeholder-product.png'}
								width={36}
								height={36}
								alt=""
								layout="intrinsic"
							/>
						</div>
						{props.item.rendered}
					</>
				) : (
					props.item.rendered
				)}
			</div>
		</li>
	)
}
